export const MAX_IMAGE_INPUT_BYTES = 15 * 1024 * 1024
export const MAX_IMAGE_EDGE = 2560
export const THUMBNAIL_EDGE = 480

export const ACCEPTED_IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/webp'] as const
export type AcceptedImageMime = (typeof ACCEPTED_IMAGE_MIMES)[number]

export type ImageInput = { type: string; size: number; name: string }
export type ImageMetadata = {
  inputMime: AcceptedImageMime
  outputMime: 'image/webp' | 'image/jpeg'
  inputBytes: number
  outputBytes: number
  thumbnailBytes: number
  width: number
  height: number
}

export type ProcessedImageBundle = {
  original: Blob
  processed: Blob
  thumbnail: Blob
  metadata: ImageMetadata
}

export function validateImageInput(input: ImageInput): { mime: AcceptedImageMime; bytes: number } {
  const extensionMime = input.type === '' ? mimeFromName(input.name) : null
  const mime = input.type || extensionMime
  if (!mime || !ACCEPTED_IMAGE_MIMES.includes(mime as AcceptedImageMime)) {
    throw new Error('Choisissez une image JPEG, PNG ou WebP décodable par votre navigateur.')
  }
  if (!Number.isFinite(input.size) || input.size <= 0) throw new Error('Cette image est vide ou illisible.')
  if (input.size > MAX_IMAGE_INPUT_BYTES) throw new Error('Cette image dépasse la limite de 15 Mo.')
  return { mime: mime as AcceptedImageMime, bytes: input.size }
}

function mimeFromName(name: string): AcceptedImageMime | null {
  const extension = name.toLocaleLowerCase('en-US').split('.').pop()
  if (extension === 'jpg' || extension === 'jpeg') return 'image/jpeg'
  if (extension === 'png') return 'image/png'
  if (extension === 'webp') return 'image/webp'
  return null
}

export function calculateOutputDimensions(
  sourceWidth: number,
  sourceHeight: number,
  maxEdge: number,
): { width: number; height: number } {
  if (sourceWidth <= 0 || sourceHeight <= 0 || maxEdge <= 0) {
    throw new Error('Dimensions d’image invalides.')
  }
  const ratio = Math.min(1, maxEdge / Math.max(sourceWidth, sourceHeight))
  return { width: Math.round(sourceWidth * ratio), height: Math.round(sourceHeight * ratio) }
}

type DecodedImage = {
  source: CanvasImageSource
  width: number
  height: number
  dispose: () => void
}

function decodeHtmlImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.decoding = 'async'
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result !== 'string') {
          reject(new Error('Cette image ne peut pas être décodée. Essayez un fichier JPEG, PNG ou WebP valide.'))
          return
        }
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error('Cette image ne peut pas être décodée. Essayez un fichier JPEG, PNG ou WebP valide.'))
        image.src = reader.result
      }
      reader.onerror = () => reject(new Error('Cette image ne peut pas être décodée. Essayez un fichier JPEG, PNG ou WebP valide.'))
      reader.readAsDataURL(file)
    }
    image.src = url
  })
}

async function decodeImage(file: Blob): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file)
      return { source: bitmap, width: bitmap.width, height: bitmap.height, dispose: () => bitmap.close() }
    } catch {
      // Older WebKit builds may expose createImageBitmap without supporting every accepted format.
    }
  }
  const image = await decodeHtmlImage(file)
  return { source: image, width: image.naturalWidth, height: image.naturalHeight, dispose: () => undefined }
}

function renderBlob(
  image: CanvasImageSource,
  dimensions: { width: number; height: number },
  mime: 'image/webp' | 'image/jpeg',
  quality: number,
): Promise<Blob | null> {
  const canvas = document.createElement('canvas')
  canvas.width = dimensions.width
  canvas.height = dimensions.height
  const context = canvas.getContext('2d')
  if (!context) return Promise.reject(new Error('La préparation de l’image est indisponible sur cet appareil.'))
  context.drawImage(image, 0, 0, dimensions.width, dimensions.height)
  return new Promise((resolve) => canvas.toBlob(resolve, mime, quality))
}

async function renderWithFallback(
  image: CanvasImageSource,
  dimensions: { width: number; height: number },
  quality: number,
): Promise<{ blob: Blob; mime: 'image/webp' | 'image/jpeg' }> {
  const webp = await renderBlob(image, dimensions, 'image/webp', quality)
  if (webp?.type === 'image/webp') return { blob: webp, mime: 'image/webp' }
  const jpeg = await renderBlob(image, dimensions, 'image/jpeg', quality)
  if (!jpeg) throw new Error('La compression de cette image a échoué.')
  return { blob: jpeg, mime: 'image/jpeg' }
}

/** Browser decoding normalizes EXIF orientation before the pixels are drawn. */
export async function processImage(file: File): Promise<ProcessedImageBundle> {
  const validated = validateImageInput(file)
  const decodable = file.type === validated.mime ? file : new Blob([file], { type: validated.mime })
  const decoded = await decodeImage(decodable)
  try {
    const mainDimensions = calculateOutputDimensions(decoded.width, decoded.height, MAX_IMAGE_EDGE)
    const thumbnailDimensions = calculateOutputDimensions(decoded.width, decoded.height, THUMBNAIL_EDGE)
    const main = await renderWithFallback(decoded.source, mainDimensions, 0.84)
    const thumbnail = await renderWithFallback(decoded.source, thumbnailDimensions, 0.76)
    return {
      original: file,
      processed: main.blob,
      thumbnail: thumbnail.blob,
      metadata: {
        inputMime: validated.mime,
        outputMime: main.mime,
        inputBytes: file.size,
        outputBytes: main.blob.size,
        thumbnailBytes: thumbnail.blob.size,
        width: mainDimensions.width,
        height: mainDimensions.height,
      },
    }
  } finally {
    decoded.dispose()
  }
}
